import type { AnimalType, ColorKey, Gender, Temperament } from './constants'
import type { Sighting } from '../types/models'

export type MapSighting = Pick<
  Sighting,
  'id' | 'animal_id' | 'type' | 'lat' | 'lng' | 'sighted_at' | 'color1'
>

export interface SightingDetailPayload {
  sighting: Sighting
  related: Sighting[]
  images: string[]
}

export interface CreateSightingInput {
  type: AnimalType
  color1: ColorKey
  color2?: ColorKey | null
  color3?: ColorKey | null
  chip_status: 'unknown' | 'none' | 'known'
  chip?: string | null
  gender?: Gender
  spayed?: 0 | 1 | null
  lat: number
  lng: number
  notes?: string | null
  temperament: Temperament
  willing_to_foster: boolean
  willing_to_adopt: boolean
  sighted_at: string
  animal_id?: number | null
  source_locale: string
}

async function readError(response: Response): Promise<string> {
  if (response.status === 413) {
    return 'Photo is still too large after compression. Try a different photo.'
  }
  try {
    const body: unknown = await response.json()
    if (body && typeof body === 'object' && 'error' in body) {
      const message = (body as { error?: unknown }).error
      if (typeof message === 'string' && message.trim()) return message
    }
  } catch {
    // Fall through to a generic status-based message.
  }
  return `Request failed (${response.status})`
}

function toMapSighting(row: Sighting): MapSighting {
  return {
    id: row.id,
    animal_id: row.animal_id,
    type: row.type,
    lat: row.lat,
    lng: row.lng,
    sighted_at: row.sighted_at,
    color1: row.color1,
  }
}

export async function fetchSightings(since?: string): Promise<MapSighting[]> {
  const params = new URLSearchParams()
  if (since) params.set('since', since)
  const query = params.toString()
  const response = await fetch(`/api/sightings${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(await readError(response))
  const body: unknown = await response.json()
  const rows =
    body && typeof body === 'object' && 'sightings' in body
      ? (body as { sightings?: Sighting[] }).sightings
      : null
  return (rows ?? []).map(toMapSighting)
}

export async function fetchSightingDetail(
  id: number,
): Promise<SightingDetailPayload> {
  const response = await fetch(`/api/sightings/${id}`, {
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(await readError(response))
  const body: unknown = await response.json()
  if (!body || typeof body !== 'object' || !('sighting' in body)) {
    throw new Error('Sighting was not returned')
  }
  const payload = body as {
    sighting?: Sighting
    related?: Sighting[]
    images?: string[]
  }
  if (!payload.sighting) throw new Error('Sighting was not returned')
  return {
    sighting: payload.sighting,
    related: payload.related ?? [],
    images: payload.images ?? [],
  }
}

export function mediaUrl(key: string) {
  return `/api/media?key=${encodeURIComponent(key)}`
}

export async function createSighting(
  input: CreateSightingInput,
): Promise<Sighting> {
  const response = await fetch('/api/sightings', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  })
  if (!response.ok) throw new Error(await readError(response))
  const body: unknown = await response.json()
  const sighting =
    body && typeof body === 'object' && 'sighting' in body
      ? (body as { sighting?: Sighting }).sighting
      : null
  if (!sighting) throw new Error('Sighting was not returned')
  return sighting
}

export async function uploadSightingImage(
  sightingId: number,
  image: File,
): Promise<void> {
  const form = new FormData()
  form.set('sighting_id', String(sightingId))
  form.set('image', image)
  const response = await fetch('/api/images', {
    method: 'POST',
    credentials: 'include',
    body: form,
  })
  if (!response.ok) throw new Error(await readError(response))
}

export async function deleteSighting(sightingId: number): Promise<void> {
  const response = await fetch(`/api/sightings/${sightingId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(await readError(response))
}

export async function updateSightingNotes(
  sightingId: number,
  notes: string | null,
  sourceLocale: string,
): Promise<Sighting> {
  const response = await fetch(`/api/sightings/${sightingId}/notes`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ notes, source_locale: sourceLocale }),
  })
  if (!response.ok) throw new Error(await readError(response))
  const body: unknown = await response.json()
  const sighting =
    body && typeof body === 'object' && 'sighting' in body
      ? (body as { sighting?: Sighting }).sighting
      : null
  if (!sighting) throw new Error('Updated sighting was not returned')
  return sighting
}
